import { MarkTypes, richTextResolver } from '@storyblok/richtext';
import type { StoryblokRichTextNode, StoryblokRichTextOptions } from '@storyblok/richtext';
import { h, createTextVNode } from 'vue';
import type { VNodeArrayChildren, VNodeChild } from 'vue';

type Vars = Record<string, string | number | boolean | null | undefined>;

type TextResolver = (text: string) => VNodeChild | VNodeArrayChildren;

type ResolverOptions = {
    transformHref?: (href: string) => string;
    textResolver?: TextResolver;
};

const TOKEN_REGEX = /\{(\w+)\}/g;

function interpolateRichText(doc: StoryblokRichTextNode, vars: Vars): StoryblokRichTextNode {
    if (!doc) return doc;
    const clone = Array.isArray(doc) ? doc.map((n) => ({ ...n })) : { ...doc };

    const walk = (node: StoryblokRichTextNode) => {
        const nodeVar = { ...node };
        if (nodeVar.type === 'text' && typeof nodeVar.text === 'string') {
            nodeVar.text = nodeVar.text.replace(TOKEN_REGEX, (_: string, key: string) => {
                const v = vars?.[key];
                return v === null || v === undefined ? `{${key}}` : String(v);
            });
        }
        if (Array.isArray(nodeVar.content)) {
            nodeVar.content = nodeVar.content.map(walk);
        }
        return nodeVar;
    };

    if (!Array.isArray(clone) && Array.isArray(clone.content)) {
        clone.content = clone.content.map(walk);
        return clone;
    }
    if (Array.isArray(clone)) {
        return clone.map(walk);
    }
    return walk(clone);
}

export function useRichTextRenderer() {
    return {
        render(doc: StoryblokRichTextNode, vars: Vars = {}, overrides?: ResolverOptions) {
            const richtext = richTextResolver({
                renderFn: h,
                textFn: overrides?.textResolver ?? createTextVNode,
                resolvers: {
                    [MarkTypes.LINK]: (node) => {
                        if (node.attrs?.linktype === 'email') {
                            const rawAddress = node.attrs?.email || node.attrs?.href || node.attrs?.url || '';
                            if (!rawAddress) return node.text?.children ?? [];

                            const href = rawAddress.startsWith('mailto:') ? rawAddress : `mailto:${rawAddress}`;

                            const children = node.text?.children ?? node.text ?? [];
                            return h('a', { href }, children);
                        }

                        const raw = node.attrs?.href || node.attrs?.url || '';
                        const anchor = node.attrs?.anchor ? `#${node.attrs.anchor}` : '';
                        const transformed = overrides?.transformHref
                            ? overrides.transformHref(raw + anchor)
                            : raw + anchor;

                        const target = !node.attrs?.uuid ? '_blank' : node.attrs?.target;
                        const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
                        const children = node.text?.children ?? node.text ?? [];

                        return h('a', { href: transformed, target, rel }, children);
                    }
                }
            });

            const interpolated = interpolateRichText(doc, vars);
            return richtext.render(interpolated);
        },

        renderToString(doc: StoryblokRichTextNode, vars: Vars = {}) {
            const interpolated = interpolateRichText(doc, vars);
            const resolver = richTextResolver();
            return resolver.render(interpolated) as string;
        }
    };
}
